// Shared scoring logic for web demo and extension

import { isCertifierRelevant, getDomainCommitmentLevel } from "./certifierRelevance";

export const DOMAIN_META = {
  "Environmental": {
    key: "environmental",
    emoji: "🌿",
    label: "Environmental",
    description: "Carbon footprint, sustainability certifications, packaging",
    premium: false,
  },
  "Labor & Fair Trade": {
    key: "labor",
    emoji: "👷",
    label: "Labor & Fair Trade",
    description: "Worker wages, fair trade certifications, supply chain transparency",
    premium: false,
  },
  "Animal Welfare": {
    key: "animal",
    emoji: "🐾",
    label: "Animal Welfare",
    description: "Cruelty-free practices, animal testing policies",
    premium: false,
  },
  "LGBTQ+ Equality": {
    key: "lgbtq",
    emoji: "🏳️‍🌈",
    label: "LGBTQ+ Equality",
    description: "Workplace equality, advocacy, HRC ratings",
    premium: true,
  },
  "Business Type": {
    key: "business_type",
    emoji: "🏪",
    label: "Business Type",
    description: "Women-owned, minority-owned, cooperative structures",
    premium: true,
  },
  "Country of Origin": {
    key: "country",
    emoji: "🌍",
    label: "Country of Origin",
    description: "Manufacturing location and local production practices",
    premium: true,
    comingSoon: true,
  },
};

export const DOMAIN_ORDER = [
  "Environmental",
  "Labor & Fair Trade",
  "Animal Welfare",
  "LGBTQ+ Equality",
  "Business Type",
  "Country of Origin",
];

export const DOMAIN_KEYS = Object.fromEntries(
  Object.entries(DOMAIN_META).map(([name, meta]) => [name, meta.key])
);

export const DEFAULT_WEIGHTS = {
  environmental: 5,
  labor: 5,
  animal: 5,
  lgbtq: 5,
  business_type: 5,
  country: 5,
};

export const DEFAULT_TOGGLES = {
  environmental: true,
  labor: true,
  animal: true,
  lgbtq: false,
  business_type: false,
  country: false,
};

export const DEFAULT_PREFERENCES = {
  weights: { ...DEFAULT_WEIGHTS },
  toggles: { ...DEFAULT_TOGGLES },
  onboardingComplete: false,
};

/**
 * Group ratings by domain name.
 * Returns { domainName -> [rating, ...] }
 */
export function groupRatingsByDomain(ratings) {
  const grouped = {};
  for (const r of ratings) {
    const name = r?.domain?.name;
    if (!name) continue;
    grouped[name] = grouped[name] || [];
    grouped[name].push(r);
  }
  return grouped;
}

/**
 * Compute per-domain score: average of Certified/Assessed scores plus any
 * Not Certified scores that resolve to 0 under the assessor-type/brand-size
 * rules — but only for certifiers that are relevant to the brand's category.
 *
 * Relevance check (applied first, before status rules):
 *   Certifier not applicable to brand category → rating skipped entirely (null)
 *
 * Not Certified resolution (relevant certifiers only):
 *   Independent assessor (any brand size)           → 0    (Rule 1)
 *   Opt-In or Opt-In with Default Fail + Enterprise → 0    (Rule 2)
 *   Opt-In or Opt-In with Default Fail + Mid-size
 *     or Small/Independent                          → null (Rule 3)
 *   Unknown assessor type or unknown brand size     → null
 *
 * Unknown status is always null — never penalises the score.
 *
 * @param {Array}  ratings       Rating objects (each with .status, .normalized_score, .certifier)
 * @param {string} brandSize     Brand's Brand Size field value
 * @param {string} brandCategory Brand's Category field value
 */
export function computeDomainScore(ratings, brandSize = "", brandCategory = "") {
  const scores = [];

  for (const r of ratings) {
    // Relevance gate — irrelevant certifiers contribute nothing
    if (!isCertifierRelevant(r.certifier?.name, brandCategory)) continue;

    if ((r.status === "Certified" || r.status === "Assessed") && r.normalized_score != null) {
      scores.push(r.normalized_score);
    } else if (r.status === "Not Certified") {
      const assessmentType = r.certifier?.assessment_type || "";
      const isOptIn =
        assessmentType === "Opt-In" ||
        assessmentType === "Opt-In with Default Fail";

      if (assessmentType === "Independent") {
        scores.push(0); // Rule 1: Independent + Not Certified = 0 always
      } else if (isOptIn && brandSize === "Enterprise") {
        // Rule 2: Opt-In + Enterprise + Not Certified
        // Apply commitment threshold before penalising — check other ratings in domain
        const otherRatings = ratings.filter(other => other.id !== r.id);
        const commitment = getDomainCommitmentLevel(otherRatings);
        if (commitment === "none" || commitment === "low") {
          scores.push(0); // no/low existing performance → penalise
        }
        // 'moderate' or 'high' → null (skip) — commitment protects against the penalty
      }
      // Rule 3: Opt-In + Mid-size/Small → null (skip)
      // Unknown assessor type → null (skip)
    }
    // Unknown status → null (skip)
  }

  if (!scores.length) return null;
  const avg = scores.reduce((s, v) => s + v, 0) / scores.length;
  return Math.round(avg * 10) / 10;
}

/**
 * Compute composite score from per-domain scores and user preferences.
 * @param {object} domainScores  { domainName -> score | null }
 * @param {object} weights       { key -> 1–10 }
 * @param {object} toggles       { key -> bool }
 * @returns {number|null}
 */
export function computeCompositeScore(domainScores, weights, toggles) {
  let weightedSum = 0;
  let totalWeight = 0;

  for (const [domainName, score] of Object.entries(domainScores)) {
    const meta = DOMAIN_META[domainName];
    if (!meta) continue;
    const key = meta.key;
    if (!toggles[key]) continue;
    if (score == null) continue;
    const w = weights[key] || 0;
    weightedSum += score * w;
    totalWeight += w;
  }

  if (totalWeight === 0) return null;
  return Math.round((weightedSum / totalWeight) * 10) / 10;
}

/**
 * Compute confidence dots for active dimensions only.
 * A dot is "filled" if the dimension has at least one Certified, Assessed, or
 * Not Certified rating from a certifier that is relevant to the brand's category.
 * Irrelevant certifiers and Unknown status never fill a dot.
 * Returns [{ key, domainName, hasDot }, ...] — one entry per active dimension.
 */
export function computeConfidenceDots(ratings, toggles, brandCategory = "") {
  const grouped = groupRatingsByDomain(ratings);
  return DOMAIN_ORDER
    .filter(name => {
      const meta = DOMAIN_META[name];
      return meta && toggles[meta.key];
    })
    .map(domainName => {
      const meta = DOMAIN_META[domainName];
      const domainRatings = grouped[domainName] || [];
      const hasDot = domainRatings.some(
        r => isCertifierRelevant(r.certifier?.name, brandCategory) &&
             (r.status === "Certified" || r.status === "Assessed" || r.status === "Not Certified")
      );
      return { key: meta.key, domainName, hasDot };
    });
}

export function getLastUpdated(ratings) {
  const vintages = ratings
    .map((r) => r.data_vintage)
    .filter(Boolean)
    .sort()
    .reverse();
  return vintages[0] || "";
}

export function loadPreferences() {
  try {
    const saved = localStorage.getItem("coral_preferences");
    if (saved) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
    }
  } catch (_) {}
  return { ...DEFAULT_PREFERENCES };
}

export function savePreferences(prefs) {
  try {
    localStorage.setItem("coral_preferences", JSON.stringify(prefs));
  } catch (_) {}
}
